<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('rulesForm.detailRewardTitle') }}</h2>
            <p class="text-muted small mb-0">{{ rule.name || ($currentLocale === 'id' ? 'Spesifikasi Misi' : 'Quest Specification') }}</p>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 mt-2 mt-md-0">
          <router-link :to="{ name: 'EditReward', params: { id: id } }" class="btn btn-primary px-3 py-2 mr-2 font-weight-bold">
            <i class="mdi mdi-lead-pencil mr-1"></i> {{ $t('common.edit') }}
          </router-link>
          <button type="button" class="btn btn-outline-danger px-3 py-2" @click="confirmDelete" :disabled="loading">
            <i class="mdi mdi-delete-outline mr-1"></i> {{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body">
          <div class="row">
            <!-- Left Column: Details -->
            <div class="col-md-7 mb-4">
              <div class="d-flex align-items-center mb-3">
                <span class="badge badge-primary px-3 py-2 mr-2 font-weight-bold text-uppercase">
                  {{ rule.type === 'onetime' ? $t('rulesForm.oneTime') : $t('rulesForm.alwaysActive') }}
                </span>
                <span class="badge badge-outline-warning px-3 py-2 font-weight-bold" v-if="rule.contest">
                  <i class="mdi mdi-trophy-variant mr-1"></i> {{ $currentLocale === 'id' ? 'Acara / Kontes' : 'Event / Contest' }}
                </span>
              </div>

              <h3 class="text-white font-weight-bold mb-3">{{ rule.name }}</h3>

              <div class="p-3 bg-dark rounded border border-secondary mb-4">
                <label class="text-muted small text-uppercase font-weight-bold d-block mb-2">{{ $t('rulesForm.description') }}</label>
                <p class="text-white mb-0" style="white-space: pre-wrap;">{{ rule.description || ($currentLocale === 'id' ? 'Tidak ada deskripsi objektif yang disertakan.' : 'No objective description provided.') }}</p>
              </div>

              <!-- Checklist SOP / Self-Checking -->
              <div class="p-3 bg-dark rounded border border-secondary mb-4" v-if="rule.checklist && rule.checklist.length > 0">
                <label class="text-muted small text-uppercase font-weight-bold d-flex align-items-center mb-2">
                  <i class="mdi mdi-checkbox-marked-circle-outline text-success mr-1"></i>
                  {{ $currentLocale === 'id' ? `Kriteria SOP & Daftar Periksa Mandiri (${rule.checklist.length} Poin)` : `SOP Criteria & Self-Checklist (${rule.checklist.length} Items)` }}
                </label>
                <div class="d-flex flex-column gap-2">
                  <div 
                    v-for="(item, idx) in rule.checklist" 
                    :key="idx" 
                    class="d-flex align-items-center p-2 rounded mb-1" 
                    style="background: rgba(255,255,255,0.05);"
                  >
                    <i class="mdi mdi-check text-success mr-2"></i>
                    <span class="text-white small">{{ item }}</span>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('rulesForm.frequency') }}</label>
                  <div class="text-white font-weight-medium text-capitalize">
                    <i class="mdi mdi-calendar-sync text-info mr-1"></i>
                    {{ resolveFrequencyLabel(rule) }}
                  </div>
                </div>
                <div class="col-sm-6 mb-3" v-if="rule.repeatable_rule && rule.repeatable_rule.ends_on">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('rulesForm.endsOn') }}</label>
                  <div class="text-white font-weight-medium">
                    <i class="mdi mdi-clock-end text-warning mr-1"></i>
                    {{ formatDate(rule.repeatable_rule.ends_on) }}
                  </div>
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('common.date') }}</label>
                  <div class="text-muted small">
                    {{ formatDate(rule.created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Rewards & Assignees -->
            <div class="col-md-5 mb-4">
              <!-- Reward Value Box -->
              <div class="p-4 bg-dark rounded border border-secondary mb-4 text-center">
                <label class="text-muted small text-uppercase font-weight-bold d-block mb-2">{{ $t('rulesForm.rewardValue') }}</label>
                <div v-if="rule.gold" class="d-flex align-items-center justify-content-center">
                  <i class="mdi mdi-circle-multiple text-warning display-4 mr-2"></i>
                  <span class="text-warning display-4 font-weight-bold">+{{ rule.gold }}</span>
                  <span class="text-warning font-weight-bold ml-2">{{ $t('rulesForm.goldCoins') }}</span>
                </div>
                <div v-else class="text-info font-weight-bold py-2">
                  <i class="mdi mdi-gift-outline mr-1"></i> {{ rule.non_gold || $t('rulesForm.nonGoldPerk') }}
                </div>
              </div>

              <!-- Assignation Box -->
              <div class="p-4 bg-dark rounded border border-secondary">
                <label class="text-muted small text-uppercase font-weight-bold d-block mb-2">{{ $t('rulesForm.targetAssignees') }}</label>
                <div v-if="assignees && assignees.length > 0">
                  <div class="mb-2">
                    <span class="badge badge-info px-2 py-1 mr-1">
                      {{ isPlayerAssignment ? $t('rulesForm.specificPlayers') : $t('rulesForm.specificSquads') }}
                    </span>
                  </div>
                  <div class="d-flex flex-wrap gap-2 mt-2">
                    <span v-for="(name, index) in assignees" :key="index" class="badge badge-secondary px-2 py-1 font-weight-normal text-white mr-1 mb-1">
                      <i :class="isPlayerAssignment ? 'mdi mdi-account mr-1' : 'mdi mdi-account-group mr-1'"></i>
                      {{ name }}
                    </span>
                  </div>
                </div>
                <div v-else class="text-muted font-weight-medium">
                  <i class="mdi mdi-earth text-success mr-1"></i>
                  {{ $t('rulesForm.allPlayers') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { deleteReward, getDetailReward } from "@/services/rewardService";
import Swal from 'sweetalert2';
import moment from 'moment';

export default {
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data: () => ({
    rule: {},
    assignees: [],
    isPlayerAssignment: true,
    loading: false,
  }),
  methods: {
    resolveFrequencyLabel(rule) {
      const isId = this.$currentLocale === 'id';
      const repeatType = (rule?.repeatable_rule?.repeat_type || rule?.frequency || '').toLowerCase();
      if (repeatType === 'daily') return isId ? 'Harian' : 'Daily';
      if (repeatType === 'weekly') return isId ? 'Mingguan' : 'Weekly';
      if (repeatType === 'monthly') return isId ? 'Bulanan' : 'Monthly';
      if (rule?.type === 'always_active' && rule?.repeatable_rule) {
        return isId ? 'Rutin' : 'Routine';
      }
      return this.$t('rulesForm.noRepeat');
    },
    back() {
      this.$router.go(-1);
    },
    async detailReward() {
      try {
        const response = await getDetailReward(this.id);
        this.rule = response.data || {};
        this.assignees = [];
        if (this.rule.assignees && this.rule.assignees.length > 0) {
          if (this.rule.assignees[0].user != null) {
            this.isPlayerAssignment = true;
            this.rule.assignees.forEach(a => {
              if (a.user) this.assignees.push(a.user.name);
            });
          } else if (this.rule.assignees[0].division != null) {
            this.isPlayerAssignment = false;
            this.rule.assignees.forEach(a => {
              if (a.division) this.assignees.push(a.division.name);
            });
          }
        }
      } catch (err) {
        console.error('Error fetching reward details:', err);
      }
    },
    confirmDelete() {
      Swal.fire({
        title: this.$t('quests.confirmDelete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.loading = true;
            await deleteReward(this.id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$t('rulesForm.deletedSuccess'),
              confirmButtonColor: '#3b82f6'
            });
            this.$router.push('/rules/rewards');
          } catch (err) {
            Swal.fire({
              icon: 'error',
              title: this.$t('common.error') || (this.$currentLocale === 'id' ? 'Terjadi Kesalahan' : 'Error'),
              text: this.$t('quests.deleteFailed') || (this.$currentLocale === 'id' ? 'Gagal menghapus misi.' : 'Failed to delete rule.'),
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY, HH:mm');
    }
  },
  mounted() {
    this.detailReward();
  }
};
</script>